import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
}

// V2 MODERN STYLE COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};

// V2 PRIMARY - Sophisticated Violet
const PRIMARY = {
  lighter: '#EDE7F6',
  light: '#9b6def',
  main: '#7635dc',
  dark: '#5a28a8',
  darker: '#3d1b73',
  contrastText: '#fff',
  // Alpha layering for V2 glass effects
  alpha3: alpha('#7635dc', 0.03),
  alpha4: alpha('#7635dc', 0.04),
  alpha6: alpha('#7635dc', 0.06),
  alpha8: alpha('#7635dc', 0.08),
  alpha10: alpha('#7635dc', 0.1),
  alpha15: alpha('#7635dc', 0.15),
  alpha20: alpha('#7635dc', 0.2),
  alpha25: alpha('#7635dc', 0.25)
};

// V2 STATUS COLORS
const STATUS = {
  paid: '#10b981',
  pending: '#f59e0b',
  paidLight: alpha('#10b981', 0.08),
  pendingLight: alpha('#f59e0b', 0.08)
};

// V2 DEEP TEXT COLOR
const TEXT_DARK = '#1e1b4b';
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff'
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800]
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800]
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff'
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  gradients: GRADIENTS,
  divider: GREY[500_24],
  chart: CHART_COLORS,
  // V2 additions
  status: STATUS,
  textDark: TEXT_DARK,
  action: {
    hover: PRIMARY.alpha8,
    selected: PRIMARY.alpha15,
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: PRIMARY.alpha20,
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: TEXT_DARK, secondary: GREY[600], disabled: GREY[500] },
    background: {
      paper: '#fff',
      default: '#f8f7fc', // V2 subtle purple tint
      neutral: GREY[200],
      gradient: `linear-gradient(135deg, ${PRIMARY.alpha3} 0%, ${PRIMARY.alpha8} 100%)`
    },
    action: { active: PRIMARY.main, ...COMMON.action }
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: GREY[500_16],
      gradient: `linear-gradient(135deg, ${alpha('#7635dc', 0.05)} 0%, ${alpha('#7635dc', 0.15)} 100%)`
    },
    action: { active: PRIMARY.light, ...COMMON.action }
  }
};

export default palette;

// Export constants for use in component overrides
export const PRIMARY_MAIN = '#7635dc';
export const PRIMARY_LIGHT = '#9b6def';
export const PRIMARY_DARK = '#5a28a8';
